import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Button,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Box,
    Grid,
    Snackbar,
    Alert,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    FilterAlt as FilterIcon,
    Clear as ClearIcon,
} from "@mui/icons-material";
import apiClient from "../Helpers/apiClient";
import BackToCPQButton from "../Components/backToCPQButton";
import PreviousPageButtonCPQ from "../Components/PreviousPageButtonCPQ";
function FreightCompanyContactTable() {
    const [contacts, setContacts] = useState([]);
    const [filteredContacts, setFilteredContacts] = useState([]);
    const [freightCompanies, setFreightCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [formData, setFormData] = useState({
        id: 0,
        freightCompanyId: "",
        contactName: "",
        email: "",
        phone: "",
        mobile: "",
        notes: "",
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    // Sorting states
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("id");
    // Filtering states
    const [filterText, setFilterText] = useState("");
    const [filterField, setFilterField] = useState("all");

    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
    }, []);

    // Apply filters whenever contacts or filter settings change
    useEffect(() => {
        applyFilters();
    }, [contacts, filterText, filterField]);

    const fetchData = async () => {
        setLoading(true);
        try {
            console.log("Fetching freight company contacts and companies...");

            // Fetch both contacts and freight companies
            const [contactsResponse, companiesResponse] = await Promise.all([
                apiClient.get("/api/FreightSystem/FreightCompanyContacts"),
                apiClient.get("/api/FreightSystem/contact-FreightCompanies"),
            ]);

            console.log("Responses received successfully!");

            if (Array.isArray(contactsResponse.data)) {
                setContacts(contactsResponse.data);
                console.log(`Successfully loaded ${contactsResponse.data.length} contacts`);
            } else {
                console.warn("Contacts response is not an array:", contactsResponse.data);
                setContacts([]);
            }

            if (Array.isArray(companiesResponse.data)) {
                setFreightCompanies(companiesResponse.data);
                console.log(`Successfully loaded ${companiesResponse.data.length} freight companies`);
            } else {
                console.warn("Companies response is not an array:", companiesResponse.data);
                setFreightCompanies([]);
            }

            setError(null);
        } catch (err) {
            console.error("Error fetching data:", err);
            setError(`Failed to load data: ${err.message}`);
            setContacts([]);
            setFreightCompanies([]);
        } finally {
            setLoading(false);
        }
    };

    const getCompanyName = (freightCompanyId) => {
        const company = freightCompanies.find((c) => (c.id || c.Id) === freightCompanyId);
        return company ? company.companyName || company.CompanyName : "Unknown Company";
    };

    const handleOpenDialog = (contact = null) => {
        if (contact) {
            setFormData({
                id: contact.id || contact.Id || 0,
                freightCompanyId: contact.freightCompanyId || contact.FreightCompanyId || "",
                contactName: contact.contactName || contact.ContactName || "",
                email: contact.email || contact.Email || "",
                phone: contact.phone || contact.Phone || "",
                mobile: contact.mobile || contact.Mobile || "",
                notes: contact.notes || contact.Notes || "",
            });
            setSelectedContact(contact);
        } else {
            setFormData({
                id: 0,
                freightCompanyId: "",
                contactName: "",
                email: "",
                phone: "",
                mobile: "",
                notes: "",
            });
            setSelectedContact(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleOpenDeleteDialog = (contact) => {
        setSelectedContact(contact);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedContact(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: name === "freightCompanyId" ? parseInt(value) || "" : value,
        });
    };

    const handleSubmit = async () => {
        try {
            // Validate required fields
            if (!formData.freightCompanyId || !formData.contactName) {
                setSnackbar({
                    open: true,
                    message: "Please fill in all required fields.",
                    severity: "error",
                });
                return;
            }

            // Convert camelCase to PascalCase to match C# model property names
            const payload = {
                Id: formData.id,
                FreightCompanyId: formData.freightCompanyId,
                ContactName: formData.contactName,
                Email: formData.email,
                Phone: formData.phone,
                Mobile: formData.mobile,
                Notes: formData.notes,
            };

            let response;
            if (selectedContact) {
                // Update existing record
                response = await apiClient.put(
                    `/api/FreightSystem/FreightCompanyContacts/${payload.Id}`,
                    payload
                );
                setSnackbar({
                    open: true,
                    message: "Contact updated successfully!",
                    severity: "success",
                });
            } else {
                // Create new record
                response = await apiClient.post("/api/FreightSystem/FreightCompanyContacts", payload);
                setSnackbar({
                    open: true,
                    message: "Contact created successfully!",
                    severity: "success",
                });
            }

            handleCloseDialog();
            fetchData();
        } catch (err) {
            console.error("Error saving contact:", err);
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to save. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleDelete = async () => {
        try {
            await apiClient.delete(
                `/api/FreightSystem/FreightCompanyContacts/${selectedContact.id || selectedContact.Id}`
            );
            setSnackbar({
                open: true,
                message: "Contact deleted successfully!",
                severity: "success",
            });
            handleCloseDeleteDialog();
            fetchData();
        } catch (err) {
            console.error("Error deleting contact:", err);
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to delete. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Sorting functionality
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const getSortedData = (data) => {
        if (!data.length) return [];

        return [...data].sort((a, b) => {
            let valueA, valueB;

            // Handle different property names
            if (orderBy === "id") {
                valueA = parseInt(a.id || a.Id || 0);
                valueB = parseInt(b.id || b.Id || 0);
                return order === "asc" ? valueA - valueB : valueB - valueA;
            } else if (orderBy === "freightCompanyId") {
                valueA = parseInt(a.freightCompanyId || a.FreightCompanyId || 0);
                valueB = parseInt(b.freightCompanyId || b.FreightCompanyId || 0);
                return order === "asc" ? valueA - valueB : valueB - valueA;
            } else if (orderBy === "companyName") {
                valueA = getCompanyName(a.freightCompanyId || a.FreightCompanyId);
                valueB = getCompanyName(b.freightCompanyId || b.FreightCompanyId);
            } else if (orderBy === "contactName") {
                valueA = a.contactName || a.ContactName || "";
                valueB = b.contactName || b.ContactName || "";
            } else if (orderBy === "email") {
                valueA = a.email || a.Email || "";
                valueB = b.email || b.Email || "";
            } else if (orderBy === "phone") {
                valueA = a.phone || a.Phone || "";
                valueB = b.phone || b.Phone || "";
            } else if (orderBy === "mobile") {
                valueA = a.mobile || a.Mobile || "";
                valueB = b.mobile || b.Mobile || "";
            }

            // For string values
            if (typeof valueA !== "number") {
                valueA = String(valueA || "").toLowerCase();
                valueB = String(valueB || "").toLowerCase();
                return order === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            }

            return order === "asc" ? valueA - valueB : valueB - valueA;
        });
    };

    // Filtering functionality
    const handleFilterChange = (event) => {
        setFilterText(event.target.value);
    };

    const handleFilterFieldChange = (field) => {
        setFilterField(field);
    };

    const clearFilter = () => {
        setFilterText("");
        setFilterField("all");
    };

    const applyFilters = () => {
        if (!filterText.trim()) {
            setFilteredContacts(contacts);
            return;
        }

        const searchTerm = filterText.toLowerCase().trim();

        const filtered = contacts.filter((contact) => {
            // Handle both camelCase and PascalCase property names from API
            const contactName = contact.contactName || contact.ContactName || "";
            const email = contact.email || contact.Email || "";
            const phone = contact.phone || contact.Phone || "";
            const mobile = contact.mobile || contact.Mobile || "";
            const notes = contact.notes || contact.Notes || "";
            const companyName = getCompanyName(contact.freightCompanyId || contact.FreightCompanyId);

            if (filterField === "all") {
                return (
                    String(contact.id || contact.Id || "").includes(searchTerm) ||
                    contactName.toLowerCase().includes(searchTerm) ||
                    email.toLowerCase().includes(searchTerm) ||
                    phone.toLowerCase().includes(searchTerm) ||
                    mobile.toLowerCase().includes(searchTerm) ||
                    notes.toLowerCase().includes(searchTerm) ||
                    companyName.toLowerCase().includes(searchTerm)
                );
            }

            // Filter by specific field
            if (filterField === "id") return String(contact.id || contact.Id || "").includes(searchTerm);
            if (filterField === "contactName") return contactName.toLowerCase().includes(searchTerm);
            if (filterField === "email") return email.toLowerCase().includes(searchTerm);
            if (filterField === "phone") return phone.toLowerCase().includes(searchTerm);
            if (filterField === "mobile") return mobile.toLowerCase().includes(searchTerm);
            if (filterField === "companyName") return companyName.toLowerCase().includes(searchTerm);
            if (filterField === "notes") return notes.toLowerCase().includes(searchTerm);

            return false;
        });

        setFilteredContacts(filtered);
    };

    // Prepare data for rendering
    const displayData = getSortedData(filteredContacts);

    return (
        <Box
            sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                padding: 4,
                paddingTop: 2,
                boxSizing: "border-box",
                overflow: "hidden",
            }}
        >
            <Typography variant="h4" gutterBottom align="center">
                Freight Company Contact Table
            </Typography>

            <Paper
                sx={{
                    width: "100%",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 2,
                        alignItems: "center",
                    }}
                >
                    {/* Filter */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "60%",
                        }}
                    >
                        <TextField
                            variant="outlined"
                            placeholder="Search..."
                            size="small"
                            fullWidth
                            value={filterText}
                            onChange={handleFilterChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: filterText ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={clearFilter} edge="end">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Tooltip title="Filter by field">
                            <Box>
                                <IconButton
                                    color={filterField !== "all" ? "primary" : "default"}
                                    aria-label="Filter options"
                                    onClick={(e) => {
                                        e.currentTarget.nextElementSibling.style.display =
                                            e.currentTarget.nextElementSibling.style.display === "none"
                                                ? "flex"
                                                : "none";
                                    }}
                                >
                                    <FilterIcon />
                                </IconButton>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        zIndex: 10,
                                        bgcolor: "background.paper",
                                        boxShadow: 3,
                                        borderRadius: 1,
                                        p: 1,
                                        display: "none",
                                        flexDirection: "column",
                                        gap: 0.5,
                                    }}
                                >
                                    {[
                                        { id: "all", label: "All Fields" },
                                        { id: "id", label: "ID" },
                                        { id: "companyName", label: "Company" },
                                        { id: "contactName", label: "Contact Name" },
                                        { id: "email", label: "Email" },
                                        { id: "phone", label: "Phone" },
                                        { id: "mobile", label: "Mobile" },
                                    ].map((option) => (
                                        <Button
                                            key={option.id}
                                            size="small"
                                            variant={filterField === option.id ? "contained" : "text"}
                                            onClick={() => {
                                                handleFilterFieldChange(option.id);
                                                // Hide the filter dropdown
                                                document.querySelector(
                                                    '[aria-label="Filter options"]'
                                                ).nextElementSibling.style.display = "none";
                                            }}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        </Tooltip>
                        {filterField !== "all" && (
                            <Typography variant="caption" sx={{ ml: 1 }}>
                                Filtering by:{" "}
                                {filterField === "id"
                                    ? "ID"
                                    : filterField === "companyName"
                                    ? "Company"
                                    : filterField === "contactName"
                                    ? "Contact Name"
                                    : filterField === "email"
                                    ? "Email"
                                    : filterField === "phone"
                                    ? "Phone"
                                    : filterField === "mobile"
                                    ? "Mobile"
                                    : ""}
                            </Typography>
                        )}
                    </Box>

                    {/* Add button */}
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add New Contact
                    </Button>
                </Box>

                <Divider />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer
                            sx={{
                                flex: 1,
                                overflow: "auto",
                            }}
                        >
                            <Table stickyHeader aria-label="freight company contact table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "id"}
                                                direction={orderBy === "id" ? order : "asc"}
                                                onClick={() => handleRequestSort("id")}
                                            >
                                                <strong>ID</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "companyName"}
                                                direction={orderBy === "companyName" ? order : "asc"}
                                                onClick={() => handleRequestSort("companyName")}
                                            >
                                                <strong>Company</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "contactName"}
                                                direction={orderBy === "contactName" ? order : "asc"}
                                                onClick={() => handleRequestSort("contactName")}
                                            >
                                                <strong>Contact Name</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "email"}
                                                direction={orderBy === "email" ? order : "asc"}
                                                onClick={() => handleRequestSort("email")}
                                            >
                                                <strong>Email</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "phone"}
                                                direction={orderBy === "phone" ? order : "asc"}
                                                onClick={() => handleRequestSort("phone")}
                                            >
                                                <strong>Phone</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "mobile"}
                                                direction={orderBy === "mobile" ? order : "asc"}
                                                onClick={() => handleRequestSort("mobile")}
                                            >
                                                <strong>Mobile</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Actions</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                {filterText
                                                    ? "No matches found. Try a different search."
                                                    : "No data available. Add your first contact."}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayData.map((contact) => (
                                            <TableRow key={contact.id || contact.Id} hover>
                                                <TableCell>{contact.id || contact.Id}</TableCell>
                                                <TableCell>
                                                    {getCompanyName(
                                                        contact.freightCompanyId || contact.FreightCompanyId
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {contact.contactName || contact.ContactName}
                                                </TableCell>
                                                <TableCell>
                                                    {contact.email || contact.Email ? (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <a
                                                                href={`mailto:${
                                                                    contact.email || contact.Email
                                                                }`}
                                                                style={{
                                                                    color: "primary.main",
                                                                    textDecoration: "none",
                                                                }}
                                                            >
                                                                {contact.email || contact.Email}
                                                            </a>
                                                        </Box>
                                                    ) : (
                                                        ""
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {contact.phone || contact.Phone ? (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <a
                                                                href={`tel:${contact.phone || contact.Phone}`}
                                                                style={{
                                                                    color: "primary.main",
                                                                    textDecoration: "none",
                                                                }}
                                                            >
                                                                {contact.phone || contact.Phone}
                                                            </a>
                                                        </Box>
                                                    ) : (
                                                        ""
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {contact.mobile || contact.Mobile ? (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <a
                                                                href={`tel:${
                                                                    contact.mobile || contact.Mobile
                                                                }`}
                                                                style={{
                                                                    color: "primary.main",
                                                                    textDecoration: "none",
                                                                }}
                                                            >
                                                                {contact.mobile || contact.Mobile}
                                                            </a>
                                                        </Box>
                                                    ) : (
                                                        ""
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleOpenDialog(contact)}
                                                        aria-label="edit"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleOpenDeleteDialog(contact)}
                                                        aria-label="delete"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box
                            sx={{
                                p: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {filterText
                                    ? `Showing ${displayData.length} of ${contacts.length} items`
                                    : `Total: ${contacts.length} items`}
                            </Typography>
                            {filterText && (
                                <Button size="small" startIcon={<ClearIcon />} onClick={clearFilter}>
                                    Clear Filter
                                </Button>
                            )}
                        </Box>
                    </>
                )}
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <BackToCPQButton />
                <PreviousPageButtonCPQ />
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>{selectedContact ? "Edit Contact" : "Add New Contact"}</DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <InputLabel>Freight Company</InputLabel>
                                    <Select
                                        name="freightCompanyId"
                                        value={formData.freightCompanyId}
                                        onChange={handleFormChange}
                                        label="Freight Company"
                                    >
                                        {freightCompanies.map((company) => (
                                            <MenuItem
                                                key={company.id || company.Id}
                                                value={company.id || company.Id}
                                            >
                                                {company.companyName || company.CompanyName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Contact Name"
                                    name="contactName"
                                    value={formData.contactName}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                    helperText="e.g., John Smith"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                    helperText="e.g., john.smith@company.com"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                    helperText="e.g., (02) 1234 5678"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Mobile"
                                    name="mobile"
                                    value={formData.mobile}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                    helperText="e.g., 0412 345 678"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                    helperText="Any additional notes or comments"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {selectedContact ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this contact? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default FreightCompanyContactTable;
